import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isNilOrEmpty } from 'ramda-extension';

import { getMessage } from '@gef-ui/localization/messages';
import ChevronDown from '@gef-ui/material-icons/ChevronDown';
import ChevronUp from '@gef-ui/material-icons/ChevronUp';
import Megaphone from '@gef-ui/material-icons/Megaphone';

import { ComponentLoader } from 'components';
import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';
import { useCollapsible } from 'features/common/Subheader/hooks';
import { joinString } from 'utils/string';

const CampaignsNearingTheEndTemplate = ({ items = EMPTY_VALUES.ARRAY, subheaderId, text, onClickFn }) => {
	const { visible, handleToggle } = useCollapsible({ id: subheaderId });

	const hasItems = items?.length > 0;

	const handleRedirect = useCallback(
		(campaignId) => () => {
			onClickFn(campaignId);
		},
		[onClickFn],
	);

	const generateCampaignLinksSeparatedByComma = useCallback(
		(campaigns) => {
			return campaigns.map((campaign, i) => (
				<Box
					whiteSpace="pre-wrap"
					sx={{
						color: 'primary.main',
					}}
				>
					<Link
						underline="none"
						onClick={handleRedirect(campaign?.id)}
						sx={{ cursor: 'pointer', fontSize: 14 }}
					>
						{joinString(' - ', [campaign?.campaignName, campaign?.communicationName])}
					</Link>
					{i + 1 !== campaigns?.length && ', '}
				</Box>
			));
		},
		[handleRedirect],
	);

	const campaignLinksWithCollapse = useMemo(() => {
		if (!visible) {
			if (items?.length > 2) {
				const [first, second, ...other] = items;
				const remainingCampaingsLength = [...other]?.length;
				const firstTwoCampaigns = [first, second];

				return (
					<>
						{generateCampaignLinksSeparatedByComma(firstTwoCampaigns)}
						<Typography variant="body2">
							{`+ ${getMessage('common:more', { count: remainingCampaingsLength })}`}
						</Typography>
					</>
				);
			} else {
				return generateCampaignLinksSeparatedByComma(items);
			}
		} else {
			return null;
		}
	}, [visible, items, generateCampaignLinksSeparatedByComma]);

	const campaingsIcon = <Megaphone sx={{ color: 'info.main', cursor: 'default' }} />;

	return (
		hasItems && (
			<ComponentLoader fetchStorePath={fetch.constants.urls.campaignsSummary}>
				<Stack sx={{ borderBottom: 1, borderBottomColor: 'grey.100', borderRadius: 1, minHeight: 50, py: 1 }}>
					<Stack
						direction="row"
						sx={{
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Stack
							direction="row"
							spacing={0.5}
							sx={{
								alignItems: 'center',
							}}
						>
							<IconButton disableRipple sx={{ cursor: 'default', ml: 1 }}>
								{campaingsIcon}
							</IconButton>
							<Stack
								spacing={0.5}
								direction="row"
								useFlexGap
								sx={{
									flexWrap: 'wrap',
								}}
							>
								<Typography variant="body2">{text}</Typography>
								{campaignLinksWithCollapse}
							</Stack>
						</Stack>
						{visible ? (
							<ChevronUp
								sx={{ color: 'primary.main', cursor: 'pointer', mr: 2 }}
								id="toggleIcon"
								onClick={handleToggle}
							/>
						) : (
							<ChevronDown
								sx={{ color: 'primary.main', cursor: 'pointer', mr: 2 }}
								id="toggleIcon"
								onClick={handleToggle}
							/>
						)}
					</Stack>
					{!isNilOrEmpty(items) && (
						<Collapse in={visible}>
							<Stack
								sx={{
									ml: 8,
								}}
							>
								<List sx={{ listStyleType: 'disc' }}>
									{items.map((campaign) => {
										const campaignFullName = joinString(' - ', [
											campaign?.campaignName,
											campaign?.communicationName,
										]);

										const campaignContactCount = `(${campaign?.contactCount})`;

										return (
											<Link
												underline="none"
												onClick={handleRedirect(campaign?.id)}
												sx={{ cursor: 'pointer' }}
											>
												<ListItem sx={{ display: 'list-item', py: 0, pl: 0 }}>
													{joinString(' ', [campaignFullName, campaignContactCount])}
												</ListItem>
											</Link>
										);
									})}
								</List>
							</Stack>
						</Collapse>
					)}
				</Stack>
			</ComponentLoader>
		)
	);
};

CampaignsNearingTheEndTemplate.propTypes = {
	items: PropTypes.array,
	onClickFn: PropTypes.func,
	subheaderId: PropTypes.string,
	text: PropTypes.string,
};

export default CampaignsNearingTheEndTemplate;
